/**
 * HomeSlider — carosello banner 1200×300 in homepage.
 *
 * Posizionato tra il ticker HostNews e la sezione "Da dove vuoi partire?".
 * Scorre automaticamente ogni 3 secondi, cliccabile verso la pagina di
 * destinazione (interna o esterna). Se non ci sono banner, non occupa spazio.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${window.__BACKEND__}/api`;
const INTERVAL = 3000;

export default function HomeSlider() {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/home-slider`, { timeout: 12000 })
      .then((r) => {
        if (alive) setSlides((r.data?.items || []).filter((s) => s.image_url));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const n = slides.length;

  useEffect(() => {
    if (n <= 1 || paused) return undefined;
    timer.current = setInterval(() => setIndex((i) => (i + 1) % n), INTERVAL);
    return () => clearInterval(timer.current);
  }, [n, paused]);

  useEffect(() => {
    if (index >= n && n > 0) setIndex(0);
  }, [index, n]);

  const go = useCallback((i) => setIndex(((i % n) + n) % n), [n]);

  const handleClick = useCallback(
    (slide) => {
      try {
        navigator.sendBeacon?.(`${API}/home-slider/${slide.id}/click`);
      } catch (_) {
        /* noop */
      }
      const url = slide.target_url || "";
      if (!url) return;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        navigate(url.startsWith("/") ? url : `/${url}`);
      }
    },
    [navigate]
  );

  if (n === 0) return null;

  return (
    <section
      className="max-w-[1280px] mx-auto px-4 md:px-8 pt-12 md:pt-14 pb-0"
      data-testid="home-slider"
      aria-label="Banner in evidenza"
    >
      <span className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#9AA1AC", letterSpacing: "0.08em" }} data-testid="home-slider-ad-label">Pubblicità</span>
      <div
        className="group relative w-full overflow-hidden rounded-[18px] shadow-[0_18px_50px_-24px_rgba(0,0,0,0.45)]"
        style={{ aspectRatio: "4 / 1", background: "#E9ECF1", border: "1px solid #E4E6EA" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* track */}
        <div
          className="flex h-full w-full"
          style={{
            transform: `translateX(-${index * 100}%)`,
            transition: "transform 600ms cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {slides.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => handleClick(s)}
              data-testid={`home-slide-${s.id}`}
              className="relative flex-none w-full h-full block cursor-pointer"
              aria-label={s.title || "Banner"}
            >
              <img
                src={s.image_url}
                alt={s.title || "Banner"}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />
            </button>
          ))}
        </div>

        {/* arrows (desktop, on hover) */}
        {n > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(index - 1)}
              data-testid="home-slider-prev"
              aria-label="Banner precedente"
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full bg-white/85 text-[#111318] shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              data-testid="home-slider-next"
              aria-label="Banner successivo"
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full bg-white/85 text-[#111318] shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {/* dots */}
        {n > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => go(i)}
                data-testid={`home-slider-dot-${i}`}
                aria-label={`Vai al banner ${i + 1}`}
                className="h-2 rounded-full transition-all"
                style={{
                  width: i === index ? 22 : 8,
                  background: i === index ? "#EA580C" : "rgba(255,255,255,0.75)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
